import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, DoorOpen, MessageCircle } from 'lucide-react';

const TopNavbar = ({
  sidebarLogo,
  menuItems = [],
  groupedTabs = [],
  activeTab,
  setActiveTab,
  iconMap = {},
  badgeMap = null,
  customerDashboardPath = null,
  canShowBackButton = false,
  rightSlot = null,
}) => {
  const navigate = useNavigate();
  const [openGroup, setOpenGroup] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    const onClickAway = (e) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(e.target)) {
        setOpenGroup(null);
      }
    };
    document.addEventListener('click', onClickAway);
    return () => document.removeEventListener('click', onClickAway);
  }, []);

  const handleGroupToggle = (label) => {
    setOpenGroup((curr) => (curr === label ? null : label));
  };

  const handleFeedbackClick = () => {
    window.open('https://forms.gle/5hp68dMXkCKCi1si6', '_blank');
  };

  const otherMenuItems = (menuItems || []).filter((i) => i.name !== 'Inicio');

  return (
    <div className="hidden lg:block fixed top-0 left-0 right-0 z-30 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-gray-200">
      <div ref={navRef} className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setActiveTab && setActiveTab('Inicio')}
            className="flex items-center gap-3 min-w-0 group"
          >
            <img src={sidebarLogo} alt="Logo" className="h-9 w-auto object-contain group-hover:opacity-90" />
            
          </button>
        </div>
        <nav className="flex-1 mx-4">
          <ul className="flex items-center gap-2 whitespace-nowrap">
            <li>
              <button
                onClick={() => setActiveTab && setActiveTab('Inicio')}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors border ${
                  activeTab === 'Inicio' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'text-gray-700 hover:bg-gray-100 border-transparent'
                }`}
                aria-current={activeTab === 'Inicio' ? 'page' : undefined}
              >
                <span className="truncate">Inicio</span>
              </button>
            </li>
            
            {groupedTabs.map((group) => group.single ? (
              <li key={group.label}>
                <button
                  onClick={() => { setActiveTab(group.items[0]); setOpenGroup(null); }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors border ${
                    activeTab === group.items[0] ? 'bg-blue-50 text-blue-700 border-blue-200' : 'text-gray-700 hover:bg-gray-100 border-transparent'
                  }`}
                  aria-current={activeTab === group.items[0] ? 'page' : undefined}
                >
                  <span className="truncate">{group.label}</span>
                </button>
              </li>
            ) : (
              <li key={group.label} className="relative">
                <button
                  onClick={() => handleGroupToggle(group.label)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors border ${
                    group.items.includes(activeTab)
                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                      : 'text-gray-700 hover:bg-gray-100 border-transparent'
                  }`}
                >
                  <span className="truncate">{group.label}</span>
                  <svg className={`w-4 h-4 ${openGroup === group.label ? 'rotate-180' : ''} transition-transform text-gray-500`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </button>
                {openGroup === group.label && (
                  <div className="absolute left-0 mt-2 min-w-[220px] max-w-[calc(100vw-1.5rem)] bg-white border border-gray-200 rounded-xl shadow-lg py-2 z-40">
                    {group.items.map((name) => (
                      <button
                        key={name}
                        onClick={() => { setActiveTab(name); setOpenGroup(null); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 ${activeTab === name ? 'text-blue-700 font-medium' : 'text-gray-700'}`}
                      >
                        <span className="inline-flex items-center justify-center w-5 h-5">
                          {iconMap[name] || null}
                        </span>
                        <span className="truncate">{name}</span>
                        {badgeMap && badgeMap[name] ? <span className="ml-auto rounded bg-gradient-to-r from-indigo-600 to-violet-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">{badgeMap[name]}</span> : null}
                      </button>
                    ))}
                    {group.label === 'Cuenta' && customerDashboardPath && (
                      <>
                        <div className="my-2 border-t border-gray-200" />
                        <button
                          onClick={() => {
                            navigate(customerDashboardPath);
                            setOpenGroup(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 text-blue-700"
                        >
                          <span className="inline-flex items-center justify-center w-5 h-5"><DoorOpen className="w-5 h-5" /></span>
                          <span className="truncate">Salir</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </li>
              
            ))}
            {otherMenuItems.map((item) => (
              <li key={item.name}>
                <button
                  onClick={() => setActiveTab(item.name)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors border ${
                    activeTab === item.name ? 'bg-blue-50 text-blue-700 border-blue-200' : 'text-gray-700 hover:bg-gray-100 border-transparent'
                  }`}
                  aria-current={activeTab === item.name ? 'page' : undefined}
                >
                  <span className="shrink-0">{item.icon}</span>
                  <span className="truncate">{item.name}</span>
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={handleFeedbackClick}
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors border text-gray-700 hover:bg-gray-100 border-transparent"
                title="Danos feedback"
              >
                <span className="truncate">Feedback</span>
              </button>
            </li>
          </ul>
        </nav>
        <div className="flex items-center gap-2">
          {rightSlot}
          {canShowBackButton && customerDashboardPath && (
            <a
              href={customerDashboardPath}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="truncate">Volver</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopNavbar;